import { useEffect, useState } from "react";
import { Button, Dropdown, Form, Label, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import { PermitRegister } from "../models/PermitRegister";
import { PermitType } from "../models/PermitType";

export interface FormData {
  title: string;
  description: string;
  permitTypeIds: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, description: string, permitTypeIds: string[], id?: string, projectId?: string) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  permitRegister?: PermitRegister;
  projectId?: string;
  availablePermitTypes?: PermitType[];
}

const EditPermitRegisterModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  permitRegister,
  projectId,
  availablePermitTypes,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({ title: "", description: "", permitTypeIds: [] });
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const permitTypeOptions =
    availablePermitTypes
      ?.map((pt) => ({ key: pt.id, text: pt.name, value: pt.id })) ?? [];

  useEffect(() => {
    if (open && permitRegister) {
      setFormData({
        title: permitRegister.title ?? "",
        description: permitRegister.description ?? "",
        permitTypeIds: [],
      });
    } else if (open) {
      setFormData({ title: "", description: "", permitTypeIds: [] });
      setFormErrors({});
      setHasSubmitted(false);
    }
  }, [open, permitRegister]);

  const validate = (data: FormData): FormError => {
    const errors: FormError = {};
    if (!data.title) errors.title = "Please enter a title";
    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    if (hasSubmitted) setFormErrors(validate(updated));
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const errors = validate(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    onSubmit(formData.title, formData.description, formData.permitTypeIds, permitRegister?.id, projectId);
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{permitRegister ? "Edit register" : "New register"}</Modal.Header>
      <Modal.Content>
        <Form error={!!submitErrors?.length}>
          <Form.Input
            required
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={formErrors.title}
          />
          <Form.TextArea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange as any}
          />
          {permitRegister?.permitTypes && permitRegister.permitTypes.length > 0 && (
            <Form.Field>
              <label>Current permit types</label>
              <div>
                {permitRegister.permitTypes.map((pt) => (
                  <Label key={pt.id} style={{ marginBottom: "4px" }}>
                    {pt.name}
                  </Label>
                ))}
              </div>
            </Form.Field>
          )}
          {permitTypeOptions.length > 0 && (
            <Form.Field
              control={Dropdown}
              label="Add permit types"
              placeholder="Select permit types to add..."
              fluid
              multiple
              selection
              search
              options={permitTypeOptions}
              value={formData.permitTypeIds}
              onChange={(_: any, { value }: any) =>
                setFormData({ ...formData, permitTypeIds: value as string[] })
              }
            />
          )}
          {submitErrors?.map((e, i) => (
            <Form.Field key={i}>
              <div className="ui error message">{e}</div>
            </Form.Field>
          ))}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary loading={submitInProgress} onClick={handleSubmit}>
          {permitRegister ? "Save" : "Create"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditPermitRegisterModal;
